import { useState, useEffect, useRef, useCallback } from "react";
import { BRAND_COLORS, MYCELIAL, EASE, DUR, THEMES, ThemeContext } from "./theme";
import { PROJECTS } from "./data/projects.js";
import { PALETTES } from "./data/palettes.js";
import { useReducedMotion, useKonami } from "./hooks";
import { GrowthSection, SubstrateGrain, ResourcePulse } from "./components/mycelial";

import HeroRSVP from "./pages/Home/components/HeroRSVP/HeroRSVP.jsx";
import NavBrandSpinner from "./pages/Home/components/NavBrandSpinner.jsx";
import FloatingContactCTA from "./pages/Home/components/FloatingContactCTA.jsx";
import FloatingSocials from "./pages/Home/components/FloatingSocials.jsx";
import MobileNav from "./pages/Home/components/MobileNav.jsx";
import ScrollToTop from "./pages/Home/components/ScrollToTop.jsx";
import ShowreelModal from "./pages/Home/components/ShowreelModal.jsx";
import ContactModal from "./pages/Home/components/ContactModal.jsx";
import ParticleField from "./pages/Home/components/ParticleField.jsx";
import Section from "./pages/Home/components/Section.jsx";
import StealthSection from "./pages/Home/components/StealthSection.jsx";
import ProjectCard from "./pages/Home/components/ProjectCard.jsx";
import LoadingScreen from "./pages/Home/components/LoadingScreen.jsx";

export default function TLAHomepage() {
  const [loading, setLoading] = useState(true);
  const [showReel, setShowReel] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [konamiTriggered, setKonamiTriggered] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [theme, setTheme] = useState("dark");
  const t = THEMES[theme];
  const reducedMotion = useReducedMotion();

  // Mycelial network state
  const networkContainerRef = useRef(null);
  const [networkVisible, setNetworkVisible] = useState(false);
  const networkRevealRef = useRef(null);
  const ctaAboutRef = useRef(null);
  const [ctaAboutVisible, setCtaAboutVisible] = useState(false);

  useKonami(useCallback(() => setKonamiTriggered(true), []));

  useEffect(() => {
    const handler = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Mycelial network IntersectionObservers
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setNetworkVisible(true); },
      { threshold: 0.05 }
    );
    if (networkRevealRef.current) observer.observe(networkRevealRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setCtaAboutVisible(true); },
      { threshold: 0.5 }
    );
    if (ctaAboutRef.current) observer.observe(ctaAboutRef.current);
    return () => observer.disconnect();
  }, []);

  const handleLogoClick = () => {
    const next = clickCount + 1;
    setClickCount(next);
    if (next >= 7) { setKonamiTriggered(true); setClickCount(0); }
  };

  return (
    <ThemeContext.Provider value={t}>
    <div style={{ background: t.bg, color: t.text, minHeight: "100vh", fontFamily: "system-ui, -apple-system, sans-serif", overflowX: "hidden", transition: "background 0.4s ease, color 0.4s ease" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap" rel="stylesheet" />
      {/* Theme-aware selection + slider styles. Static keyframes/media queries live in global.css. */}
      <style>{`
        ::selection { background: ${t.selectionBg}; color: ${t.selectionText}; }
        input[type="range"] { background: ${t.sliderTrack}; }
        input[type="range"]::-webkit-slider-thumb { border-color: ${t.sliderBorder}; }
        input[type="range"]::-moz-range-thumb { border-color: ${t.sliderBorder}; }
      `}</style>
      <SubstrateGrain />

      {loading && (
        <LoadingScreen
          onComplete={() => setLoading(false)}
          accentColor={PALETTES.default.pivot}
          accentGlow={PALETTES.default.glow}
        />
      )}

      <div className="floating-cta-desktop"><FloatingContactCTA ready={!loading} onContactClick={() => setContactOpen(true)} /></div>
      <div className="floating-socials-desktop"><FloatingSocials ready={!loading} /></div>
      <ScrollToTop />

      {/* Modals must live outside the page-entrance wrapper below — that
          wrapper has a transform, which would break position:fixed and shift
          the modal off-center relative to the viewport. */}
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />

      {/* Page entrance wrapper — fades in after loading */}
      <div style={{
        opacity: loading ? 0 : 1,
        transform: loading ? "translateY(12px)" : "translateY(0)",
        transition: "opacity 0.8s ease 0.1s, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.1s",
      }}>

      <ParticleField />

      {showReel && <ShowreelModal onClose={() => setShowReel(false)} />}

      {konamiTriggered && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 2000,
          background: "rgba(0,0,0,0.95)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: 24, animation: "fadeIn 0.3s ease", cursor: "pointer",
        }} onClick={() => setKonamiTriggered(false)}>
          <div style={{ fontSize: 80, animation: "spinSlow 3s linear infinite" }}>⚽</div>
          <span style={{ color: BRAND_COLORS.cyan, fontSize: 24, fontWeight: 700, fontFamily: "monospace", letterSpacing: 4 }}>THE PINT PROMISE</span>
          <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 14, fontFamily: "Georgia, serif", maxWidth: 400, textAlign: "center", lineHeight: 1.8 }}>
            Challenge us to a match. Winner buys the first pint. Standing invitation. Always.
          </span>
          <span style={{ color: "rgba(255,255,255,0.62)", fontSize: 13, fontFamily: "monospace", marginTop: 20 }}>Click anywhere to close</span>
        </div>
      )}

      {/* ── NAV ── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        padding: "14px 28px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: scrollY > 60 ? t.navBg : "transparent",
        backdropFilter: scrollY > 60 ? "blur(24px)" : "none",
        borderBottom: scrollY > 60 ? `1px solid ${t.borderSubtle}` : "none",
        transition: "all 0.4s ease",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <NavBrandSpinner onClick={handleLogoClick} />
        </div>
        <div className="nav-links" style={{ display: "flex", gap: 28, alignItems: "center" }}>
          {["Work", "About", "Contact", "Careers"].map((item) => (
            <a key={item} href={item === "Careers" ? "/careers" : `#${item.toLowerCase()}`} style={{
              color: t.textMid, textDecoration: "none", fontSize: 13,
              fontWeight: 500, letterSpacing: 0.5, transition: "color 0.3s", cursor: "pointer",
            }}
              onMouseEnter={e => { e.target.style.color = t.text; }}
              onMouseLeave={e => { e.target.style.color = t.textMid; }}
            >{item}</a>
          ))}
        </div>
        <button
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          aria-label="Toggle theme"
          style={{
            width: 36, height: 36, borderRadius: 8,
            border: `1px solid ${t.borderStrong}`,
            background: t.surfaceHover,
            color: t.textMid,
            fontSize: 14, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.3s ease",
            flexShrink: 0,
          }}
        >{theme === "dark" ? "☀" : "☽"}</button>
        <MobileNav />
      </nav>

      {/* ── HERO: RSVP AS PRIMARY EXPERIENCE ── */}
      <section style={{
        minHeight: "100vh",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        position: "relative", padding: "clamp(80px, 12vh, 120px) 0 clamp(40px, 6vh, 60px)",
      }}>
        <div style={{
          position: "absolute", width: "70vw", height: "70vw", maxWidth: 900, maxHeight: 900,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${BRAND_COLORS.purple}0D 0%, transparent 70%)`,
          top: "50%", left: "50%",
          transform: `translate(-50%, -50%) translateY(${scrollY * -0.08}px)`,
          pointerEvents: "none", animation: "breathe 8s ease-in-out infinite",
        }} />

        <HeroRSVP ready={!loading} />

        <div style={{
          position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
          opacity: scrollY > 100 ? 0 : 0.35, transition: "opacity 0.5s",
        }}>
          <span style={{ fontSize: 9, fontFamily: "monospace", letterSpacing: 3, color: t.textMid }}>SCROLL</span>
          <div style={{ width: 1, height: 16, background: t.textLow, animation: "pulse 2s ease infinite" }} />
        </div>
      </section>

      {/* ── STEALTH MODE TEASER ── */}
      <StealthSection />

      {/* ── OUR WORK — SELECTED PROJECTS ── */}
      <section id="work" style={{
        padding: "clamp(72px, 10vw, 128px) clamp(24px, 5vw, 80px)",
        maxWidth: 1200,
        margin: "0 auto",
        position: "relative",
        perspective: "1800px",
      }}>
        <Section>
          <div style={{ marginBottom: 48 }}>
            <span style={{ color: BRAND_COLORS.cyan, fontSize: 12, fontFamily: "'DM Mono', monospace", letterSpacing: 4, textTransform: "uppercase" }}>Our Work</span>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 48px)", fontWeight: 800, marginTop: 16, letterSpacing: -1, lineHeight: 1.2, color: t.text }}>
              Built with athletes,<br /><span style={{ color: t.textLow }}>brands, and rights holders.</span>
            </h2>
          </div>
        </Section>
        <Section>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))",
            gap: 36,
          }}>
            {PROJECTS.map((p, i) => (
              <ProjectCard key={p.id} project={p} index={i} />
            ))}
          </div>
        </Section>
      </section>

      {/* ── CAPITAL SOLUTIONS & ATHLETE SUPPORT ── */}
      <section style={{ padding: "clamp(72px, 10vw, 128px) clamp(24px, 5vw, 80px)", borderTop: `1px solid ${t.borderSubtle}`, maxWidth: 1200, margin: "0 auto" }}>
        <Section>
          <div style={{ display: "flex", gap: 48, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ flex: "1 1 340px" }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: `linear-gradient(135deg, ${BRAND_COLORS.red}22, ${BRAND_COLORS.orange}22)`, border: `1px solid ${BRAND_COLORS.red}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, marginBottom: 24 }}>◆</div>
              <span style={{ color: BRAND_COLORS.red, fontSize: 12, fontFamily: "monospace", letterSpacing: 4 }}>CAPITAL SOLUTIONS</span>
              <h3 style={{ fontSize: "clamp(22px, 3vw, 28px)", fontWeight: 700, marginTop: 12, marginBottom: 16, letterSpacing: -0.5, color: t.text }}>A network of capital,<br />built on trust.</h3>
              <p style={{ color: t.textMid, fontSize: 15, lineHeight: 1.8, fontFamily: "Georgia, serif" }}>We have access to a network of capital — family offices, VCs, angels, and a trusted network to get things done.</p>
            </div>
            <div style={{ flex: "1 1 340px" }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: `linear-gradient(135deg, ${BRAND_COLORS.blue}22, ${BRAND_COLORS.cyan}22)`, border: `1px solid ${BRAND_COLORS.blue}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, marginBottom: 24 }}>★</div>
              <span style={{ color: BRAND_COLORS.blue, fontSize: 12, fontFamily: "monospace", letterSpacing: 4 }}>ATHLETE BUILT · ATHLETE LED</span>
              <h3 style={{ fontSize: "clamp(22px, 3vw, 28px)", fontWeight: 700, marginTop: 12, marginBottom: 16, letterSpacing: -0.5, color: t.text }}>From the pitch.<br />For the arena.</h3>
              <p style={{ color: t.textMid, fontSize: 15, lineHeight: 1.8, fontFamily: "Georgia, serif" }}>We don't have to imagine being on the pitch or feeling the pressure of the game. We have been there — and we're there for the ones in the arena.</p>
            </div>
          </div>
        </Section>
      </section>

      {/* ── ABOUT — GRASSROOTS MANIFESTO ── */}
      <section id="about" style={{
        position: "relative", zIndex: 2,
        padding: "clamp(72px, 10vw, 128px) clamp(24px, 5vw, 80px)",
        borderTop: `1px solid ${t.borderSubtle}`,
        background: t.bgAlt,
      }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <GrowthSection phase={3}>
            <span style={{
              color: t.fruitingCoral, fontSize: 11,
              fontFamily: "'DM Mono', monospace", letterSpacing: 5,
              textTransform: "uppercase", fontWeight: 400,
            }}>GRASSROOTS</span>
            <h3 style={{
              fontSize: "clamp(26px, 4vw, 44px)", fontWeight: 800,
              marginTop: 16, marginBottom: 28,
              letterSpacing: -1, lineHeight: 1.15,
              color: t.text,
            }}>
              From the ground up.<br />
              <span style={{ color: t.textFaint }}>
                Not from a pitch deck.
              </span>
            </h3>
          </GrowthSection>
          <GrowthSection phase={3} delay={0.1}>
            <p style={{ fontSize: 15, lineHeight: 1.9, color: t.textMid, fontFamily: "Georgia, serif", marginBottom: 20 }}>
              Football taught us performance. Code taught us precision. Data
              taught us truth. We saved 20% on SaaS bills and built a company
              around never accepting bad deals.
            </p>
            <p style={{ fontSize: 15, lineHeight: 1.9, color: t.textMid, fontFamily: "Georgia, serif", marginBottom: 20 }}>
              Now we build for defence
              communications. For children's books. For anyone bold enough
              to demand better.
            </p>
            <p style={{ fontSize: 15, lineHeight: 1.9, color: t.textMid, fontFamily: "Georgia, serif" }}>
              We give it back to the people who build it. That's the
              cooperative model. That's the root system. That's TLA.
            </p>
          </GrowthSection>
        </div>
      </section>

      {/* ── THE HORIZON (About CTA with resource pulse) ── */}
      <section style={{
        position: "relative", zIndex: 2,
        padding: "clamp(72px, 10vw, 128px) clamp(24px, 5vw, 80px)",
        borderTop: `1px solid ${t.borderSubtle}`,
        textAlign: "center", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", top: "50%", left: "50%",
          width: "140vw", height: "140vw",
          transform: "translate(-50%, -50%)",
          background: `radial-gradient(ellipse at center, ${MYCELIAL.enzymeAmber}06 0%, transparent 60%)`,
          pointerEvents: "none",
        }} />
        <div style={{ position: "relative", maxWidth: 640, margin: "0 auto" }}>
          <GrowthSection phase={3}>
            <span style={{
              color: t.sporeGold, fontSize: 11,
              fontFamily: "'DM Mono', monospace", letterSpacing: 5,
              textTransform: "uppercase", fontWeight: 400,
            }}>THE HORIZON</span>
            <h3 style={{
              fontSize: "clamp(26px, 5vw, 52px)", fontWeight: 900,
              marginTop: 16, marginBottom: 20,
              letterSpacing: -1.5, lineHeight: 1.08,
              color: t.text,
            }}>
              Three letters.<br />No boundaries.
            </h3>
            <p style={{
              fontSize: "clamp(14px, 1.8vw, 17px)", lineHeight: 1.85,
              color: t.textLow, fontFamily: "Georgia, serif",
            }}>
              From grassroots to the edge of what's possible. The same
              network that connects a goalkeeper's instincts to an
              engineer's precision connects a children's book to a defence
              contract. The mycelium doesn't care about categories.
              Neither do we.
            </p>
          </GrowthSection>
          <GrowthSection phase={3} delay={0.15} style={{ marginTop: 40, position: "relative", display: "inline-block" }}>
            <div ref={ctaAboutRef} style={{ position: "relative", display: "inline-block" }}>
              <ResourcePulse color={t.sporeGold} visible={ctaAboutVisible} />
              <a
                href="#contact"
                onMouseEnter={e => {
                  e.currentTarget.style.background = t.sporeGold;
                  e.currentTarget.style.color = t.bg;
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = `0 8px 24px ${t.sporeGold}25`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = t.sporeGold;
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
                style={{
                  display: "inline-block", padding: "13px 32px",
                  border: `1.5px solid ${t.sporeGold}60`, borderRadius: 6,
                  color: t.sporeGold, fontSize: 12,
                  fontFamily: "'DM Mono', monospace", letterSpacing: 3,
                  textTransform: "uppercase", textDecoration: "none",
                  transition: `all ${DUR.quick} ${EASE.tipExtension}`,
                  fontWeight: 500,
                }}
              >Challenge us</a>
            </div>
          </GrowthSection>
        </div>
      </section>

      {/* ── CTA STRIP ── */}
      <section id="contact" style={{ padding: "clamp(72px, 10vw, 128px) clamp(24px, 5vw, 80px)", borderTop: `1px solid ${t.borderSubtle}`, textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", width: "50vw", height: "50vw", maxWidth: 600, maxHeight: 600, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND_COLORS.blue}12 0%, transparent 70%)`, top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "none" }} />
        <Section>
          <h2 style={{ fontSize: "clamp(32px, 5vw, 64px)", fontWeight: 800, letterSpacing: -2, marginBottom: 20, position: "relative", color: t.text }}>
            Ready to build<br />
            <span style={{ background: `linear-gradient(135deg, ${BRAND_COLORS.purple}, ${BRAND_COLORS.cyan})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>something bold?</span>
          </h2>
          <p style={{ color: t.textMid, fontSize: 16, fontFamily: "Georgia, serif", marginBottom: 40, position: "relative" }}>Or at the very least, challenge us to a match.</p>
          <div style={{ display: "flex", justifyContent: "center", position: "relative" }}>
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              style={{ padding: "clamp(14px, 2vw, 18px) clamp(28px, 5vw, 44px)", borderRadius: 40, border: "none", background: t.text, color: t.bg, fontSize: "clamp(14px, 1.5vw, 16px)", fontWeight: 700, cursor: "pointer", transition: "all 0.3s", letterSpacing: 0.5, minHeight: 48 }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 32px rgba(255,255,255,0.15)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
            >Get in Touch</button>
          </div>
        </Section>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ padding: "40px clamp(24px, 5vw, 80px)", borderTop: `1px solid ${t.borderSubtle}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20, maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ color: t.textMid, fontSize: 13, fontFamily: "monospace" }}>© 2026</span>
        </div>
        <span
          style={{ color: "transparent", fontSize: "clamp(12px, 1vw, 13px)", fontFamily: "monospace", cursor: "default", transition: "color 1s ease", userSelect: "none", textAlign: "center", flex: "1 1 auto", overflow: "hidden", textOverflow: "ellipsis" }}
          onMouseEnter={e => { e.target.style.color = t.textMid; }}
          onMouseLeave={e => { e.target.style.color = "transparent"; }}
        >TLA IS A THREE LETTER ACRONYM. WHICH IS ITSELF A THREE LETTER ACRONYM.</span>
        <div style={{ color: t.textMid, fontSize: 13, fontFamily: "monospace" }}>tlastudios.com</div>
      </footer>
      </div>{/* end page entrance wrapper */}
    </div>
    </ThemeContext.Provider>
  );
}
